import api from './api'
import type { Client, ClientContact } from '@/types'

export interface ClientProposal {
  id: string
  proposalNumber: string
  title: string
  estimatedValue: number
  status: string
  createdAt: string
  sentToClientAt: string | null
  viewedAt: string | null
  signedAt: string | null
  expiresAt: string | null
}

export interface ClientDetail {
  client: Client
  stats: {
    totalRevenue: number
    totalBids: number
    bidsWon: number
    closeRate: number
  }
  proposals: ClientProposal[]
}

export interface ContactPayload {
  firstName: string
  lastName?: string
  email?: string
  phone?: string
  title?: string
  isPrimary?: boolean
}

export const clientsService = {
  async list() {
    const { data } = await api.get('/clients')
    return data.data as Client[]
  },

  async get(id: string) {
    const { data } = await api.get(`/clients/${id}`)
    return data.data as ClientDetail
  },

  async create(payload: {
    name?: string
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    company?: string
    isResidence?: boolean
    address?: string
    city?: string
    state?: string
    zip?: string
  }) {
    const { data } = await api.post('/clients', payload)
    return data.data as Client
  },

  async import(rows: Array<{
    name?: string
    firstName?: string
    lastName?: string
    company?: string
    email?: string
    phone?: string
    address?: string
    city?: string
    state?: string
    zip?: string
  }>) {
    const { data } = await api.post('/clients/import', { rows })
    return data.data as { imported: number; errors: string[] }
  },

  async update(id: string, payload: {
    name?: string
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
    company?: string
    isResidence?: boolean
    address?: string
    city?: string
    state?: string
    zip?: string
  }) {
    const { data } = await api.put(`/clients/${id}`, payload)
    return data.data as Client
  },

  // ── Contacts ──────────────────────────────────────────────────────────

  async listContacts(clientId: string) {
    const { data } = await api.get(`/clients/${clientId}/contacts`)
    return data.data as ClientContact[]
  },

  async createContact(clientId: string, payload: ContactPayload) {
    const { data } = await api.post(`/clients/${clientId}/contacts`, payload)
    return data.data as ClientContact
  },

  async updateContact(clientId: string, contactId: string, payload: Partial<ContactPayload>) {
    const { data } = await api.put(`/clients/${clientId}/contacts/${contactId}`, payload)
    return data.data as ClientContact
  },

  async deleteContact(clientId: string, contactId: string) {
    await api.delete(`/clients/${clientId}/contacts/${contactId}`)
  },

  async deleteClient(clientId: string) {
    await api.delete(`/clients/${clientId}`)
  },
}
